<script lang="ts" setup>
	const props = defineProps<{
		loading?: boolean
		columns: TableColumn[]
	}>()
	const tableLoader = useTemplateRef<HTMLDivElement>('tableLoader')
	const tableWrapper = useTemplateRef<HTMLDivElement>('wrapper')
	const { loading, columns } = toRefs(props)

	const totalRecords = shallowRef<number[]>([1, 2, 3])

	const scrollTarget = shallowRef<HTMLDivElement | null>(null)

	const updateScrollTarget = () => {
		if (!tableLoader.value) return
		const el = tableLoader.value.querySelector('.ant-table-body, .ant-table-content')
		if (el && el !== scrollTarget.value) {
			scrollTarget.value = el as HTMLDivElement
		} else if (!el && tableLoader.value !== scrollTarget.value) {
			scrollTarget.value = tableLoader.value
		}
	}

	onMounted(() => {
		updateScrollTarget()
		if (tableLoader.value) {
			const observer = new MutationObserver(updateScrollTarget)
			observer.observe(tableLoader.value, { childList: true, subtree: true })
		}
	})

	const { isOverflowing, scrollElement, showScrollLeft } = useOverflowDetection(scrollTarget, {
		root: tableWrapper,
		observe: loading
	})

	onMounted(() => {
		if (Array.isArray(props.columns) && totalRecords.value.length < props.columns.length) {
			for (let index = 0; index < props.columns.length; index++) {
				totalRecords.value.push(index)
			}
		}
	})
</script>

<template>
	<div ref="wrapper" class="w-full">
		<div
			class="relative z-20 flex w-full justify-end py-2 pr-2.5 transition-all"
			:class="{ flex: isOverflowing, hidden: !isOverflowing }"
		>
			<button
				type="button"
				class="flex items-center gap-0.5 text-[14px] leading-[18px] text-gray-500"
				@click="() => scrollElement()"
			>
				<tools-icon
					dir="arrows"
					icon="double-greater-than"
					:svg-class="`${!showScrollLeft ? 'rotate-[180deg]' : ''} cursor-pointer`"
					:height="18"
					:width="18"
					class="moveX custom-transition-2"
				/>
				Scroll {{ showScrollLeft ? 'left' : 'right' }}
			</button>
		</div>

		<div ref="tableLoader" class="custom-scrollbar custom-transition-2 relative overflow-x-auto pb-10">
			<a-table v-if="loading" :columns :data-source="totalRecords" :pagination="false">
				<template #bodyCell>
					<loader-skeleton :count="1" class="h-[40px] rounded-lg" :height="48" />
				</template>
			</a-table>
			<div :class="{ 'absolute -z-0 opacity-0': loading }">
				<slot :columns />
			</div>
		</div>
	</div>
</template>

<style scoped>
	@keyframes moveX {
		0% {
			transform: translateX(0);
		}

		10%,
		30% {
			transform: translateX(-6px);
		}

		35%,
		50% {
			transform: translateX(-6px);
		}

		55%,
		70% {
			transform: translateX(-6px);
			transform-origin: 0 5px;
		}

		80%,
		100% {
			transform: translateX(0px);
		}
	}
	.moveX {
		animation: moveX 5s linear 2s infinite;
		transition: 1s linear;
		transform-origin: center;
		transform-style: preserve-3d;
	}
	.animate-x {
		animation: moveX 2s linear infinite;
		transition: 1s linear;
		transform-origin: center;
		transform-style: preserve-3d;
	}
</style>
